import React, { useCallback, useState } from 'react'
import type { ExistingAttachment } from '../../../../../components/AttachmentManager'
import { downloadRemoteFile } from '../../../../../utils/downloadRemoteFile'
import { openSignedAttachment, getSignedAttachmentUrl } from '../../../../../utils/openSignedAttachment'
import { uploadFileToGeneralFiles } from '../api'
import { FILE_SIZE_LIMIT } from '../constants'

export const useAttachmentHandler = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [existingAttachments, setExistingAttachments] = useState<ExistingAttachment[]>([])
  const [existingFileIds, setExistingFileIds] = useState<number[]>([])
  const [removedFileIds, setRemovedFileIds] = useState<number[]>([])

  const uploadPendingFiles = useCallback(async (): Promise<number[]> => {
    if (selectedFiles.length === 0) return []
    const uploadedIds = await Promise.all(selectedFiles.map((file) => uploadFileToGeneralFiles(file)))
    setSelectedFiles([])
    return uploadedIds
  }, [selectedFiles])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter((f) => {
      if (f.size > FILE_SIZE_LIMIT) { alert(`Arquivo ${f.name} excede 200MB.`); return false }
      return true
    })
    setSelectedFiles((prev) => [...prev, ...files])
    e.target.value = ''
  }

  const handleSelectFromGallery = useCallback((files: ExistingAttachment[]) => {
    setExistingAttachments((prev) => {
      const existingIds = new Set(prev.map((a) => a.id))
      const toAdd = files.filter((f) => !existingIds.has(f.id))
      return [...prev, ...toAdd]
    })
    setExistingFileIds((prev) => {
      const existingSet = new Set(prev)
      const newIds = files.map((f) => f.id).filter((id) => !existingSet.has(id))
      return [...prev, ...newIds]
    })
  }, [])

  const handleRemoveFile = (i: number) =>
    setSelectedFiles((prev) => prev.filter((_, idx) => idx !== i))

  const handlePreviewFile = useCallback((file: File) => {
    const url = URL.createObjectURL(file)
    window.open(url, '_blank')
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }, [])

  const handleDownloadFile = useCallback((file: File) => {
    const url = URL.createObjectURL(file)
    void downloadRemoteFile(url, file.name).finally(() => URL.revokeObjectURL(url))
  }, [])

  const handleRemoveExistingAttachment = (id: number) => {
    if (window.confirm('Tem certeza que deseja remover este anexo?')) {
      setExistingAttachments((prev) => prev.filter((a) => a.id !== id))
      setRemovedFileIds((prev) => [...prev, id])
    }
  }

  const handleViewAttachment = (a: ExistingAttachment) => {
    const source = a.filePath || a.fileUrl
    if (source) {
      void openSignedAttachment(source)
    }
  }

  const handleDownloadAttachment = async (a: ExistingAttachment) => {
    const source = a.filePath || a.fileUrl
    if (!source) return
    const signed = await getSignedAttachmentUrl(source, true, a.originalName)
    void downloadRemoteFile(signed, a.originalName)
  }

  return {
    selectedFiles,
    existingAttachments,
    existingFileIds,
    removedFileIds,
    setSelectedFiles,
    setExistingAttachments,
    setExistingFileIds,
    setRemovedFileIds,
    uploadPendingFiles,
    handleFileChange,
    handleSelectFromGallery,
    handleRemoveFile,
    handlePreviewFile,
    handleDownloadFile,
    handleRemoveExistingAttachment,
    handleViewAttachment,
    handleDownloadAttachment,
  }
}
